/**
 * Auto-fix duplicated Site Studio modal IDs/triggers for HSL Items List Modal component.
 *
 * Site Studio renders modals near <body> (not inside the component DOM tree),
 * so we must match CTAs (inside component) to modals (in document) by repeater index.
 */
(function (Drupal, once) {
  function getInstanceNamespace(component) {
    const instanceClass = Array.from(component.classList).find((c) =>
      c.startsWith("ssa-component-instance-"),
    );

    if (instanceClass)
      return instanceClass.replace("ssa-component-instance-", "");

    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return `id-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }

  function normalizeText(str) {
    return (str || "").replace(/\s+/g, " ").trim().toLowerCase();
  }

  /**
   * Find a modal in the document that belongs to the given repeater index.
   * Strategy:
   *  - Primary: match heading data-ssa-field-uuid containing `_${index}_`
   *  - Secondary: also match heading text equals CTA text (helps avoid collisions)
   */
  function hasIndexToken(uuid, index) {
    return [`_${index}_`, `_${index + 1}_`].some((token) =>
      uuid.includes(token),
    );
  }

  function findModalForTrigger(trigger, index, ctaText) {
    const originalTarget = trigger.getAttribute("data-modal-open");
    if (originalTarget) {
      const originalModal = document.getElementById(originalTarget);
      if (originalModal?.matches(".hsl-items-list-modal__modal")) {
        return originalModal;
      }
    }

    const wantedText = normalizeText(ctaText);

    // Candidate modals: all our modals on the page.
    const modals = document.querySelectorAll(".hsl-items-list-modal__modal");

    // 1) Strong match: uuid contains _{index}_ and heading text matches.
    for (const modal of modals) {
      const heading = modal.querySelector(
        ".a11y-modal__heading[data-ssa-field-uuid]",
      );
      if (!heading) continue;

      const uuid = heading.getAttribute("data-ssa-field-uuid") || "";
      if (!hasIndexToken(uuid, index)) continue;

      if (wantedText && normalizeText(heading.textContent) === wantedText) {
        return modal;
      }
    }

    // 2) Medium match: uuid contains _{index}_ (no text check).
    for (const modal of modals) {
      const heading = modal.querySelector(
        ".a11y-modal__heading[data-ssa-field-uuid]",
      );
      if (!heading) continue;

      const uuid = heading.getAttribute("data-ssa-field-uuid") || "";
      if (hasIndexToken(uuid, index)) {
        return modal;
      }
    }

    // 3) Fallback: heading text equals CTA text.
    if (wantedText) {
      for (const modal of modals) {
        const heading = modal.querySelector(".a11y-modal__heading");
        if (!heading) continue;

        if (normalizeText(heading.textContent) === wantedText) {
          return modal;
        }
      }
    }

    return null;
  }

  function updateInteractionTargets(modal, modalId) {
    // Update the "Entendi" link / any element that has data-interaction-modifiers
    // (attribute contains HTML-escaped JSON, so string replace is safest).
    modal.querySelectorAll("[data-interaction-modifiers]").forEach((el) => {
      const raw = el.getAttribute("data-interaction-modifiers");
      if (!raw) return;

      // Replace interactionTarget value inside the attribute.
      const updated = raw.replace(
        /"interactionTarget":"item-solucao-modal-[^"]*"/g,
        `"interactionTarget":"${modalId}"`,
      );

      el.setAttribute("data-interaction-modifiers", updated);
    });
  }

  Drupal.behaviors.hslItemsListModalAutofix = {
    attach(context) {
      once(
        "hsl-items-list-modal-autofix",
        ".hsl-items-list-modal",
        context,
      ).forEach((component) => {
        const ns = getInstanceNamespace(component);

        const triggers = component.querySelectorAll(
          ".hsl-items-list-modal__tag[data-modal-open]",
        );

        if (!triggers.length) return;

        triggers.forEach((trigger, i) => {
          const n = i + 1;
          const modalId = `item-solucao-modal-${ns}-${n}`;
          const triggerId = `item-solucao-trigger-${ns}-${n}`;

          const ctaText = trigger.textContent;

          // Find the corresponding modal rendered near <body>.
          const modal = findModalForTrigger(trigger, i, ctaText);
          if (!modal) {
            // Keep original target to avoid pointing to a missing modal.
            if (!trigger.getAttribute("id")) {
              trigger.setAttribute("id", triggerId);
            }
            return;
          }

          // Fix trigger.
          trigger.setAttribute("data-modal-open", modalId);
          trigger.setAttribute("id", triggerId);

          // Fix modal id.
          modal.setAttribute("id", modalId);

          // Fix aria-labelledby + heading id to be consistent.
          const heading = modal.querySelector(".a11y-modal__heading");
          if (heading) {
            const headingId = `${modalId}_heading`;
            heading.setAttribute("id", headingId);
            modal.setAttribute("aria-labelledby", headingId);
          }

          // Fix internal toggle-modifier targets (e.g., "Entendi" CTA).
          updateInteractionTargets(modal, modalId);
        });
      });
    },
  };
})(Drupal, once);
